package com.schoolconnect.app.feature.studentenrollment

import androidx.compose.ui.graphics.Color
import korlibs.io.file.std.openAsZip
import korlibs.io.stream.openAsync

private data class WorkbookSheetRef(
    val name: String,
    val relationId: String
)

private val ExpectedEnrollmentSheets = listOf("Instructions", "Students", "Parents", "Reference Values")
private val ExpectedEnrollmentHeaders = linkedMapOf(
    "Students" to listOf("Admission No", "Class", "Section", "Student Name", "Roll No.", "Gender", "DOB", "Subject Codes", "Blood Group"),
    "Parents" to listOf("Admission No", "Primary Contact Name", "Primary Contact Mobile", "Primary Contact Email", "Primary Contact Relationship", "Secondary Contact Name", "Secondary Contact Mobile", "Secondary Contact Email", "Secondary Contact Relationship"),
    "Reference Values" to listOf("", "Gender", "Blood Group", "Relationship")
)

private data class SheetParseResult<T>(
    val rows: List<T>,
    val errorRows: List<StudentEnrollmentErrorRow>,
    val messages: List<String>
)

private val SubjectChipPalette = listOf(
    Color(0xFFE2ECFF) to Color(0xFF2563EB),
    Color(0xFFE6F7EE) to Color(0xFF18A765),
    Color(0xFFF0E4FF) to Color(0xFF7C3AED),
    Color(0xFFFFEFE0) to Color(0xFFEA7A18),
    Color(0xFFFFE5EE) to Color(0xFFF43F5E),
    Color(0xFFE4F7F6) to Color(0xFF0EA5B7),
    Color(0xFFE7F7E4) to Color(0xFF65A30D),
    Color(0xFFFFEED6) to Color(0xFFEA7A18)
)

suspend fun parseStudentEnrollmentWorkbook(
    bytes: ByteArray,
    fileName: String
): Result<StudentEnrollmentWorkbookPreview> = runCatching {
    val workbook = parseXlsxWorkbookSheets(bytes).getOrElse { throw it }
    val sheetMap = workbook.entries.associateBy { normalizeSheetName(it.key) }
    val structureErrors = validateEnrollmentWorkbookStructure(workbook)
    if (structureErrors.isNotEmpty()) {
        return@runCatching StudentEnrollmentWorkbookPreview(
            fileName = fileName.ifBlank { "Import_Students_Enrollment_Template.xlsx" },
            uploadedOn = "Just now",
            studentRecords = emptyList(),
            parentRecords = emptyList(),
            errorRows = emptyList(),
            validationMessages = emptyList(),
            structureErrors = structureErrors
        )
    }
    val students = parseStudentsSheet(sheetMap["students"]?.value.orEmpty())
    val parents = parseParentsSheet(sheetMap["parents"]?.value.orEmpty())
    val validationMessages = buildList {
        addAll(students.messages)
        addAll(parents.messages)
        if (sheetMap["students"] == null) add("Students sheet not found in workbook.")
        if (sheetMap["parents"] == null) add("Parents sheet not found in workbook.")
    }.distinct()

    StudentEnrollmentWorkbookPreview(
        fileName = fileName.ifBlank { "Import_Students_Enrollment_Template.xlsx" },
        uploadedOn = "Just now",
        studentRecords = students.rows,
        parentRecords = parents.rows,
        errorRows = emptyList(),
        validationMessages = validationMessages
    )
}

private fun validateEnrollmentWorkbookStructure(workbook: Map<String, List<List<String>>>): List<String> {
    val issues = mutableListOf<String>()
    val actualSheets = workbook.keys.toList()
    if (actualSheets != ExpectedEnrollmentSheets) {
        val missing = ExpectedEnrollmentSheets.filterNot { it in actualSheets }
        val unexpected = actualSheets.filterNot { it in ExpectedEnrollmentSheets }
        if (missing.isNotEmpty()) issues += "Missing tab(s): ${missing.joinToString(", ")}."
        if (unexpected.isNotEmpty()) issues += "Unexpected tab(s): ${unexpected.joinToString(", ")}."
        if (missing.isEmpty() && unexpected.isEmpty()) {
            issues += "Tabs are in the wrong order. Required order: ${ExpectedEnrollmentSheets.joinToString(" → ")}."
        } else {
            issues += "Required tab order: ${ExpectedEnrollmentSheets.joinToString(" → ")}."
        }
    }
    ExpectedEnrollmentHeaders.forEach { (sheetName, expectedHeaders) ->
        val actualName = actualSheets.firstOrNull { it == sheetName } ?: return@forEach
        val actualHeaders = workbook[actualName].orEmpty().firstOrNull().orEmpty().map(String::trim)
        if (actualHeaders != expectedHeaders) {
            issues += "“$sheetName” headers must be: ${expectedHeaders.joinToString(" | ") { it.ifBlank { "(blank first cell)" } }}."
            if (actualHeaders.isEmpty()) issues += "“$sheetName” is missing its header row."
            else issues += "Found: ${actualHeaders.joinToString(" | ") { it.ifBlank { "(blank)" } }}."
        }
    }
    return issues.distinct()
}

private fun parseStudentsSheet(
    rows: List<List<String>>
): SheetParseResult<StudentEnrollmentStudentRecordUiModel> {
    val headers = rows.firstOrNull().orEmpty()
    val index = headerIndexMap(headers)
    val messages = mutableListOf<String>()
    val errorRows = mutableListOf<StudentEnrollmentErrorRow>()
    val parsedRows = mutableListOf<StudentEnrollmentStudentRecordUiModel>()

    validateHeaders(
        sheetName = "Students",
        headers = headers,
        expectedHeaders = listOf(
            "Admission No",
            "Class",
            "Section",
            "Student Name",
            "Roll No.",
            "Gender",
            "DOB",
            "Subject Codes",
            "Blood Group"
        ),
        messages = messages
    )

    rows.drop(1).forEachIndexed { rowOffset, row ->
        val excelRow = rowOffset + 2
        val admissionNo = rowValue(row, index, "admission_no")
        val className = rowValue(row, index, "class")
        val section = rowValue(row, index, "section")
        val studentName = rowValue(row, index, "student_name")
        val rollNo = rowValue(row, index, "roll_no")
        val gender = rowValue(row, index, "gender")
        val dob = rowValue(row, index, "dob")
        val subjectCodes = rowValue(row, index, "subject_codes")
        val bloodGroup = rowValue(row, index, "blood_group")

        if (listOf(admissionNo, className, section, studentName, rollNo, gender, dob, subjectCodes, bloodGroup).all { it.isBlank() }) {
            return@forEachIndexed
        }

        parsedRows += StudentEnrollmentStudentRecordUiModel(
            rowNo = excelRow - 1,
            admissionNo = admissionNo,
            className = className,
            section = section,
            studentName = studentName,
            rollNo = rollNo,
            gender = gender.ifBlank { "Unknown" },
            dob = dob,
            subjectCodes = parseSubjectChips(subjectCodes),
            bloodGroup = bloodGroup
        )

    }

    return SheetParseResult(parsedRows, errorRows, messages)
}

private fun parseParentsSheet(
    rows: List<List<String>>
): SheetParseResult<StudentEnrollmentParentRecordUiModel> {
    val headers = rows.firstOrNull().orEmpty()
    val index = headerIndexMap(headers)
    val messages = mutableListOf<String>()
    val errorRows = mutableListOf<StudentEnrollmentErrorRow>()
    val parsedRows = mutableListOf<StudentEnrollmentParentRecordUiModel>()

    validateHeaders(
        sheetName = "Parents",
        headers = headers,
        expectedHeaders = listOf(
            "Admission No",
            "Primary Contact Name",
            "Primary Contact Mobile",
            "Primary Contact Email",
            "Primary Contact Relationship",
            "Secondary Contact Name",
            "Secondary Contact Mobile",
            "Secondary Contact Email",
            "Secondary Contact Relationship"
        ),
        messages = messages
    )

    rows.drop(1).forEachIndexed { rowOffset, row ->
        val excelRow = rowOffset + 2
        val admissionNo = rowValue(row, index, "admission_no")
        val primaryContactName = rowValue(row, index, "primary_contact_name")
        val primaryContactMobile = rowValue(row, index, "primary_contact_mobile")
        val primaryContactEmail = rowValue(row, index, "primary_contact_email")
        val primaryContactRelationship = rowValue(row, index, "primary_contact_relationship")
        val secondaryContactName = rowValue(row, index, "secondary_contact_name")
        val secondaryContactMobile = rowValue(row, index, "secondary_contact_mobile")
        val secondaryContactEmail = rowValue(row, index, "secondary_contact_email")
        val secondaryContactRelationship = rowValue(row, index, "secondary_contact_relationship")

        if (listOf(admissionNo, primaryContactName, primaryContactMobile, primaryContactEmail, primaryContactRelationship, secondaryContactName, secondaryContactMobile, secondaryContactEmail, secondaryContactRelationship).all { it.isBlank() }) {
            return@forEachIndexed
        }

        parsedRows += StudentEnrollmentParentRecordUiModel(
            rowNo = excelRow - 1,
            admissionNo = admissionNo,
            primaryContactName = primaryContactName,
            primaryContactMobile = primaryContactMobile,
            primaryContactEmail = primaryContactEmail,
            primaryContactRelationship = primaryContactRelationship,
            secondaryContactName = secondaryContactName,
            secondaryContactMobile = secondaryContactMobile,
            secondaryContactEmail = secondaryContactEmail,
            secondaryContactRelationship = secondaryContactRelationship
        )

    }

    return SheetParseResult(parsedRows, errorRows, messages)
}

private fun parseSubjectChips(value: String): List<StudentEnrollmentSubjectChipUiModel> {
    val codes = value.split('|').map { it.trim() }.filter { it.isNotBlank() }
    return codes.mapIndexed { index, code ->
        val palette = SubjectChipPalette[absoluteValue(code.lowercase().hashCode() + index) % SubjectChipPalette.size]
        StudentEnrollmentSubjectChipUiModel(
            label = code,
            background = palette.first,
            contentColor = palette.second
        )
    }
}

private fun validateHeaders(
    sheetName: String,
    headers: List<String>,
    expectedHeaders: List<String>,
    messages: MutableList<String>
) {
    val normalizedHeaders = headers.map { normalizeHeader(it) }
    val normalizedExpected = expectedHeaders.map { normalizeHeader(it) }
    if (normalizedHeaders.isEmpty()) {
        messages += "$sheetName sheet is empty."
        return
    }
    if (normalizedHeaders.take(expectedHeaders.size) != normalizedExpected) {
        messages += "$sheetName headers do not match the template."
    }
}

private fun headerIndexMap(headers: List<String>): Map<String, Int> {
    val output = linkedMapOf<String, Int>()
    headers.forEachIndexed { index, header ->
        val normalized = normalizeHeader(header)
        if (normalized.isNotBlank() && normalized !in output) {
            output[normalized] = index
        }
    }
    return output
}

private fun rowValue(row: List<String>, index: Map<String, Int>, key: String): String {
    val direct = index[key]
    if (direct != null) return row.getOrNull(direct).orEmpty().trim()
    val aliases = when (key) {
        "admission_no" -> listOf("admissionno", "admissionnumber")
        "student_name" -> listOf("studentname")
        "primary_contact_name" -> listOf("primarycontactname")
        "primary_contact_mobile" -> listOf("primarycontactmobile")
        "primary_contact_email" -> listOf("primarycontactemail")
        "primary_contact_relationship" -> listOf("primarycontactrelationship")
        "secondary_contact_name" -> listOf("secondarycontactname")
        "secondary_contact_mobile" -> listOf("secondarycontactmobile")
        "secondary_contact_email" -> listOf("secondarycontactemail")
        "secondary_contact_relationship" -> listOf("secondarycontactrelationship")
        "subject_codes" -> listOf("subjectcodes")
        "blood_group" -> listOf("bloodgroup")
        "roll_no" -> listOf("rollno", "rollnumber")
        else -> emptyList()
    }
    aliases.forEach { alias ->
        val aliasIndex = index[alias]
        if (aliasIndex != null) return row.getOrNull(aliasIndex).orEmpty().trim()
    }
    return ""
}

private fun normalizeHeader(value: String): String {
    return value.trim().lowercase().replace(Regex("[^a-z0-9]+"), "_").trim('_')
}

private fun normalizeSheetName(value: String): String {
    return value.trim().lowercase().replace(Regex("\\s+"), " ")
}

private fun absoluteValue(value: Int): Int {
    return if (value == Int.MIN_VALUE) Int.MAX_VALUE else kotlin.math.abs(value)
}

private suspend fun parseXlsxWorkbookSheets(bytes: ByteArray): Result<Map<String, List<List<String>>>> = runCatching {
    val zipRoot = bytes.openAsync().openAsZip()
    try {
        val workbookXml = zipRoot["xl/workbook.xml"].readBytes().decodeToString()
        val workbookRelsXml = zipRoot["xl/_rels/workbook.xml.rels"].readBytes().decodeToString()
        val sharedStringsXml = runCatching { zipRoot["xl/sharedStrings.xml"].readBytes().decodeToString() }.getOrNull()

        val sheetRefs = parseWorkbookSheetRefs(workbookXml)
        val relationTargets = parseWorkbookRelations(workbookRelsXml)
        val sharedStrings = parseSharedStrings(sharedStringsXml)

        val parsed = linkedMapOf<String, List<List<String>>>()
        sheetRefs.forEach { sheet ->
            val target = relationTargets[sheet.relationId] ?: return@forEach
            val sheetPath = normalizeSheetPath(target)
            val sheetXml = runCatching { zipRoot[sheetPath].readBytes().decodeToString() }.getOrNull() ?: return@forEach
            parsed[sheet.name] = parseSheetRows(sheetXml, sharedStrings)
        }
        parsed
    } finally {
        zipRoot.vfs.close()
    }
}

private fun parseWorkbookSheetRefs(xml: String): List<WorkbookSheetRef> {
    val sheetRegex = Regex("<(?:[A-Za-z0-9_]+:)?sheet\\b([^>]*)/?>")
    return sheetRegex.findAll(xml).mapNotNull { match ->
        val attrs = parseAttributes(match.groupValues[1])
        val name = attrs["name"].orEmpty().trim()
        val relationId = (attrs["r:id"] ?: attrs["id"]).orEmpty().trim()
        if (name.isBlank() || relationId.isBlank()) null else WorkbookSheetRef(name, relationId)
    }.toList()
}

private fun parseWorkbookRelations(xml: String): Map<String, String> {
    val relationRegex = Regex("<Relationship\\b([^>]*)/?>")
    val output = linkedMapOf<String, String>()
    relationRegex.findAll(xml).forEach { match ->
        val attrs = parseAttributes(match.groupValues[1])
        val id = attrs["Id"].orEmpty().trim()
        val target = attrs["Target"].orEmpty().trim()
        if (id.isNotBlank() && target.isNotBlank()) output[id] = target
    }
    return output
}

private fun parseSharedStrings(xml: String?): List<String> {
    if (xml.isNullOrBlank()) return emptyList()
    val siRegex = Regex("<(?:[A-Za-z0-9_]+:)?si\\b[^>]*>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?si>", setOf(RegexOption.IGNORE_CASE))
    val textRegex = Regex("<(?:[A-Za-z0-9_]+:)?t\\b[^>]*>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?t>", setOf(RegexOption.IGNORE_CASE))
    return siRegex.findAll(xml).map { si ->
        textRegex.findAll(si.groupValues[1]).joinToString("") { unescapeXml(it.groupValues[1]) }.trim()
    }.toList()
}

private fun parseSheetRows(xml: String, sharedStrings: List<String>): List<List<String>> {
    val rows = mutableListOf<List<String>>()
    val rowRegex = Regex("<(?:[A-Za-z0-9_]+:)?row\\b[^>]*>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?row>", setOf(RegexOption.IGNORE_CASE))
    // Match self-closing cells first. Otherwise the paired-cell alternative can
    // treat <c ... /> as an opening tag and consume the next cell's contents.
    val cellRegex = Regex("<(?:[A-Za-z0-9_]+:)?c\\b([^>]*)/>|<(?:[A-Za-z0-9_]+:)?c\\b([^>]*)>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?c>", setOf(RegexOption.IGNORE_CASE))
    val valueRegex = Regex("<(?:[A-Za-z0-9_]+:)?v\\b[^>]*>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?v>", setOf(RegexOption.IGNORE_CASE))
    val textRegex = Regex("<(?:[A-Za-z0-9_]+:)?t\\b[^>]*>([\\s\\S]*?)</(?:[A-Za-z0-9_]+:)?t>", setOf(RegexOption.IGNORE_CASE))

    rowRegex.findAll(xml).forEach { rowMatch ->
        val rowContent = rowMatch.groupValues[1]
        val rowCells = mutableListOf<String>()
        var activeCol = 0

        cellRegex.findAll(rowContent).forEach { cellMatch ->
            val attrsRaw = if (cellMatch.groupValues[1].isNotBlank()) cellMatch.groupValues[1] else cellMatch.groupValues[2]
            val inner = cellMatch.groupValues[3]
            val attrs = parseAttributes(attrsRaw)
            val type = attrs["t"].orEmpty()
            val col = referenceToColumnIndex(attrs["r"].orEmpty()) ?: activeCol

            val value = if (inner.isBlank()) {
                ""
            } else when (type) {
                "s" -> {
                    val index = valueRegex.find(inner)?.groupValues?.get(1)?.trim()?.toIntOrNull()
                    if (index == null) "" else sharedStrings.getOrNull(index).orEmpty()
                }
                "b" -> {
                    val raw = valueRegex.find(inner)?.groupValues?.get(1)?.trim().orEmpty()
                    if (raw == "1") "TRUE" else "FALSE"
                }
                "inlineStr" -> textRegex.findAll(inner).joinToString("") { unescapeXml(it.groupValues[1]) }.trim()
                else -> unescapeXml(valueRegex.find(inner)?.groupValues?.get(1).orEmpty()).trim()
            }

            while (rowCells.size <= col) rowCells.add("")
            rowCells[col] = value
            activeCol = col + 1
        }

        rows.add(rowCells)
    }
    return rows
}

private fun parseAttributes(raw: String): Map<String, String> {
    if (raw.isBlank()) return emptyMap()
    val attrRegex = Regex("([A-Za-z_][A-Za-z0-9_:\\-.]*)\\s*=\\s*\"([^\"]*)\"")
    val output = linkedMapOf<String, String>()
    attrRegex.findAll(raw).forEach { match ->
        output[match.groupValues[1]] = unescapeXml(match.groupValues[2])
    }
    return output
}

private fun normalizeSheetPath(target: String): String {
    var normalized = target.replace('\\', '/').trim()
    while (normalized.startsWith("../")) normalized = normalized.removePrefix("../")
    normalized = normalized.removePrefix("/")
    return if (normalized.startsWith("xl/")) normalized else "xl/$normalized"
}

private fun referenceToColumnIndex(cellRef: String): Int? {
    if (cellRef.isBlank()) return null
    val letters = cellRef.takeWhile { it.isLetter() }
    if (letters.isBlank()) return null
    var index = 0
    letters.uppercase().forEach { ch ->
        index = index * 26 + (ch.code - 'A'.code + 1)
    }
    return index - 1
}

private fun unescapeXml(value: String): String {
    return value
        .replace("&amp;", "&")
        .replace("&lt;", "<")
        .replace("&gt;", ">")
        .replace("&quot;", "\"")
        .replace("&apos;", "'")
}
